import React, { memo, useImperativeHandle, useRef } from "react";
import { Transform } from "../../transform";
import { ZoneSelectionBoxHandlers } from "../zone-selection-box/zone-selection-box-handlers";

import "./guideline-overlay.css";

// Import Vector2 and LineSegment2 from shared snap utilities
type LineSegment2 = eXpress.creator.snap.LineSegment2;
type Vector2 = eXpress.creator.snap.Vector2;
const Vector2 = eXpress.creator.snap.Vector2;
const LineSegment2 = eXpress.creator.snap.LineSegment2;

export interface ILine {
	lineSegment: LineSegment2;
	type?: "page-edge" | "zone-edge";
}

/**
 * The type of zone transformation being performed.
 * Determines both the snapping behavior (in use-snapping.ts) and which guidelines to display.
 * Matches desktop's TransformType enum (mobile uses a simplified version without per-corner resize variants).
 */
export enum TransformType {
	Move,
	Resize,
	Rotate,
}

// Maximum snapping distance in DIP (Device Independent Pixels)
// Desktop uses cssMaxSnappingDistance = 5 CSS pixels converted to DIP via dipToCssScale
// For mobile, we use 10 DIP as a reasonable default (similar to desktop's typical values)
const MAX_SNAP_DISTANCE_DIP = 10;

// Maximum rotation difference for snapping in degrees
// Desktop uses defaultRotation = 4 degrees, converted to normalized dot product
// cos(4°) ≈ 0.9976, so minDot should be approximately 0.998
const MAX_ROTATION_DEGREES = 4;

// Helper function to convert rotation angle to normalized dot product (like desktop's rotationToNormalizedDot)
function rotationToNormalizedDot(angleDeg: number): number {
	return Math.cos((Math.PI * angleDeg) / 180);
}

export interface IGuidelineOverlayRef {
	el: SVGSVGElement;
	drawGuidelines(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
		currentHandle: ZoneSelectionBoxHandlers,
		transformType: TransformType,
	): ILine[];
	removeGuidelines(): void;
}

/**
 * Corner IDs matching desktop's CornerId enum in snap.ts.
 * Ordered so that adjacent numbers form adjacent edges.
 */
export enum CornerId {
	TopLeft = 0,
	TopRight = 1,
	BottomRight = 2,
	BottomLeft = 3,
}

/**
 * Get Snapping rectangle.
 * We snap to zone including border thickness, excluding frame effect.
 */
export function zoneTransformToSnappingRect(zoneTransform: eXpress.core.IZoneTransform) {
	const { borderThickness } = zoneTransform;
	return {
		top: zoneTransform.top - borderThickness,
		left: zoneTransform.left - borderThickness,
		width: zoneTransform.width + borderThickness * 2,
		height: zoneTransform.height + borderThickness * 2,
		angle: zoneTransform.angle,
	};
}

/**
 * Maximum angle difference (in degrees) for two angles to be considered "compatible".
 * This tolerance accounts for floating point precision issues.
 * Same as desktop's `angleDifferentLimit` of 0.05 degrees.
 */
const ANGLE_DIFFERENCE_LIMIT = 0.05;

/**
 * Check if two zone angles are compatible for snapping.
 * Zones can only snap to other zones whose angle differs by a multiple of 90 degrees.
 *
 * For example:
 * - A zone with angle 0° can snap to zones with angles 0°, 90°, 180°, 270°
 * - A zone with angle 45° can snap to zones with angles 45°, 135°, 225°, 315°
 * - A zone with angle 30° can only snap to zones with angles 30°, 120°, 210°, 300°
 *
 * This matches desktop's logic in ZoneTransformer.updateAlignmentLines().
 *
 * @param angle1 - First zone's angle in degrees
 * @param angle2 - Second zone's angle in degrees
 * @returns true if the angles are compatible (differ by multiple of 90°)
 */
function areAnglesCompatible(angle1: number, angle2: number): boolean {
	// Add 720 to ensure positive value before modulo (same as desktop)
	const dAngleDeg = 720 + angle1 - angle2;
	const remainder = dAngleDeg % 90;

	// Check if remainder is close to 0 or close to 90 (both mean it's a multiple of 90)
	return remainder < ANGLE_DIFFERENCE_LIMIT || remainder > 90 - ANGLE_DIFFERENCE_LIMIT;
}

/**
 * Calculate the actual corner positions considering rotation.
 * Uses the same approach as desktop's getCornerPoints in snap.ts:
 * - TopLeft is at (left, top) - the rotation origin
 * - Other corners are calculated by rotating vectors around TopLeft
 *
 * This is the single source of truth for rotated corner calculation.
 * Exported for use in use-snapping.ts.
 *
 * @param rect - The rectangle with angle (rotation in degrees)
 * @returns Array of 4 corner points in order: [TopLeft, TopRight, BottomRight, BottomLeft]
 */
export function getRotatedCorners(rect: eXpress.math.IRectWithAngle) {
	const angleRad = (rect.angle * Math.PI) / 180;
	const topLeft = Vector2.create(rect.left, rect.top);

	// Use the same rotation approach as desktop: Vector2.create(dx, dy).rotate(angleRad, origin)
	const topRightVec = Vector2.create(rect.width, 0).rotate(angleRad, topLeft);
	const bottomRightVec = Vector2.create(rect.width, rect.height).rotate(angleRad, topLeft);
	const bottomLeftVec = Vector2.create(0, rect.height).rotate(angleRad, topLeft);

	const corners = [
		new eXpress.math.Point(topLeft.x, topLeft.y),
		new eXpress.math.Point(topRightVec.x, topRightVec.y),
		new eXpress.math.Point(bottomRightVec.x, bottomRightVec.y),
		new eXpress.math.Point(bottomLeftVec.x, bottomLeftVec.y),
	];

	// Free temporary Vector2 objects to avoid cache growth
	topLeft.free();
	topRightVec.free();
	bottomRightVec.free();
	bottomLeftVec.free();

	return corners as [
		topLeft: eXpress.math.Point,
		topRight: eXpress.math.Point,
		bottomRight: eXpress.math.Point,
		bottomLeft: eXpress.math.Point,
	];
}

/**
 * Extract the 4 edges of a rectangle as LineSegment2s.
 * This is similar to desktop's extractLines function in snap.ts.
 * The edges are the actual rotated edges, not axis-aligned.
 *
 * @param rect - The rectangle with angle
 * @param type - The line type ("page-edge" or "zone-edge")
 * @returns Array of 4 ILine objects representing the edges
 */
function extractEdgesFromRect(rect: eXpress.math.IRectWithAngle, type: "page-edge" | "zone-edge") {
	const corners = getRotatedCorners(rect);
	const lines: ILine[] = [];

	// Create 4 edges connecting adjacent corners (like desktop's extractLines)
	// Order: Top edge, Right edge, Bottom edge, Left edge
	// IMPORTANT: Pass maxDist and minDot parameters to enable proper snapping
	// - maxDist: maximum distance in DIP for snapping to occur
	// - minDot: minimum dot product for angle alignment (rotation tolerance)
	const maxDist = MAX_SNAP_DISTANCE_DIP;
	const minDot = rotationToNormalizedDot(MAX_ROTATION_DEGREES);

	for (let i = 0; i < 4; i++) {
		const start = corners[i];
		const end = corners[(i + 1) % 4];

		const startVec = Vector2.create(start.left, start.top);
		const endVec = Vector2.create(end.left, end.top);

		// Create LineSegment2 with proper maxDist and minDot (like desktop's extractLines)
		lines.push({
			lineSegment: LineSegment2.create(startVec, endVec, maxDist, minDot),
			type,
		});
	}

	return lines as unknown as readonly [
		topEdge: ILine,
		rightEdge: ILine,
		bottomEdge: ILine,
		leftEdge: ILine,
	];
}

/**
 * Get the corner IDs that move during a resize operation for a given handle.
 * This is used to determine which corners to check for snapping line visibility.
 *
 * @param handle - The resize handle being dragged
 * @returns Array of CornerId values for the corners that move
 */
function getMovingCornersForHandle(handle: ZoneSelectionBoxHandlers): CornerId[] {
	switch (handle) {
		// Corner handles: only that corner moves
		case ZoneSelectionBoxHandlers.TopLeft:
			return [CornerId.TopLeft];
		case ZoneSelectionBoxHandlers.TopRight:
			return [CornerId.TopRight];
		case ZoneSelectionBoxHandlers.BottomLeft:
			return [CornerId.BottomLeft];
		case ZoneSelectionBoxHandlers.BottomRight:
			return [CornerId.BottomRight];
		// Side handles: two corners move
		case ZoneSelectionBoxHandlers.Top:
			return [CornerId.TopLeft, CornerId.TopRight];
		case ZoneSelectionBoxHandlers.Right:
			return [CornerId.TopRight, CornerId.BottomRight];
		case ZoneSelectionBoxHandlers.Bottom:
			return [CornerId.BottomLeft, CornerId.BottomRight];
		case ZoneSelectionBoxHandlers.Left:
			return [CornerId.TopLeft, CornerId.BottomLeft];
		case ZoneSelectionBoxHandlers.None:
		case ZoneSelectionBoxHandlers.All:
		case ZoneSelectionBoxHandlers.Sides:
		case ZoneSelectionBoxHandlers.Corners:
		default:
			return [];
	}
}

/**
 * Compute the angular alignment (absolute dot product) between a zone edge and a snap line.
 * A dot product of 1.0 means the edge and line are perfectly parallel (best alignment).
 * A dot product of 0.0 means they are perpendicular (no alignment).
 *
 * Returns -1 if the alignment is below the line's minDot threshold (not aligned enough).
 */
function getEdgeLineAlignment(
	edgeStart: eXpress.math.Point,
	edgeEnd: eXpress.math.Point,
	line: ILine,
): number {
	const edgeStartVec = Vector2.create(edgeStart.left, edgeStart.top);
	const edgeDirection = Vector2.create(edgeEnd.left, edgeEnd.top)
		.subtract(edgeStartVec)
		.normalize();
	const lineDirection = line.lineSegment.getVectorDiff();
	let dot = edgeDirection.dot(lineDirection);
	if (dot < 0) dot = -dot;

	edgeStartVec.free();
	edgeDirection.free();
	lineDirection.free();

	return dot > line.lineSegment.minDot ? dot : -1;
}

/**
 * Get the squared distance from a point to the closest point on a snap line (treated as infinite).
 * Uses squared distance to avoid sqrt for efficient comparison.
 */
function getDistanceToLineSq(edgeStart: eXpress.math.Point, line: ILine): number {
	const edgeStartVec = Vector2.create(edgeStart.left, edgeStart.top);
	const closestPoint = line.lineSegment.closestPoint(edgeStartVec, true);
	const distanceSq = edgeStartVec.distanceSq(closestPoint);
	edgeStartVec.free();
	closestPoint.free();
	return distanceSq;
}

/**
 * Determine which snap lines to visually highlight during a rotate operation.
 *
 * Two-phase approach (same as desktop's getAlignmentLines):
 * 1. **Proximity phase**: For each of the 4 zone edges, find the closest angularly-aligned snap line
 *    within `maxDist` DIP. This shows lines that the zone is actively snapping to.
 * 2. **Angle fallback**: If no lines are close enough, find the single best angularly-aligned line
 *    across ALL lines (ignoring distance). This gives a visual hint of which line the zone
 *    is "approaching" as the user rotates, matching desktop's `getAlignmentLineByAngle`.
 */
function getRotationAlignedLines(
	lines: readonly ILine[],
	zoneRect: eXpress.math.IRectWithAngle,
	maxDist: number,
): Set<ILine> {
	const rotatedCorners = getRotatedCorners(zoneRect);
	const edgePairs: ReadonlyArray<[CornerId, CornerId]> = [
		[CornerId.TopLeft, CornerId.TopRight],
		[CornerId.TopRight, CornerId.BottomRight],
		[CornerId.BottomRight, CornerId.BottomLeft],
		[CornerId.BottomLeft, CornerId.TopLeft],
	];

	const alignedLines = new Set<ILine>();
	const maxDistSq = maxDist * maxDist;

	// Phase 1: Proximity — find the closest angularly-aligned line per edge
	for (const [startCornerId, endCornerId] of edgePairs) {
		const edgeStart = rotatedCorners[startCornerId];
		const edgeEnd = rotatedCorners[endCornerId];
		let bestLine: ILine | undefined;
		let bestDistanceSq = Number.POSITIVE_INFINITY;

		for (const line of lines) {
			if (getEdgeLineAlignment(edgeStart, edgeEnd, line) < 0) continue;
			const distanceSq = getDistanceToLineSq(edgeStart, line);
			if (distanceSq > maxDistSq) continue;
			if (distanceSq < bestDistanceSq) {
				bestDistanceSq = distanceSq;
				bestLine = line;
			}
		}

		if (bestLine) {
			alignedLines.add(bestLine);
		}
	}

	if (alignedLines.size > 0) {
		return alignedLines;
	}

	// Phase 2: Angle fallback — show the closest angularly-aligned line regardless of distance.
	// Only checks the top edge (TopLeft→TopRight), matching desktop's getAlignmentLineByAngle.
	// Selects by closest distance (like desktop's getAlignmentLine), not by best dot product.
	// This shows the nearest snap line the zone is approaching, giving better visual feedback.
	const edgeStart = rotatedCorners[CornerId.TopLeft];
	const edgeEnd = rotatedCorners[CornerId.TopRight];
	let bestLine: ILine | undefined;
	let bestDistanceSq = Number.POSITIVE_INFINITY;

	for (const line of lines) {
		const dot = getEdgeLineAlignment(edgeStart, edgeEnd, line);
		if (dot < 0) continue;

		// Among all angularly-aligned lines, pick the closest one by distance
		const distanceSq = getDistanceToLineSq(edgeStart, line);
		if (distanceSq < bestDistanceSq) {
			bestDistanceSq = distanceSq;
			bestLine = line;
		}
	}

	if (bestLine) {
		alignedLines.add(bestLine);
	}

	return alignedLines;
}

/**
 * Extract guidelines from a zone's edges.
 * This function extracts the 4 edges of the zone as LineSegment2s,
 * properly accounting for rotation (like desktop's extractLines).
 *
 * @param zoneRect - The zone rectangle with angle
 * @returns Array of ILine objects representing the zone's edges
 */
function extractZoneGuidelines(zoneRect: eXpress.math.IRectWithAngle) {
	return extractEdgesFromRect(zoneRect, "zone-edge");
}

/**
 * Extract guidelines from the spread's edges.
 * The spread is always axis-aligned (no rotation), so we use angle: 0.
 *
 * @param spread - The spread to extract edges from
 * @returns Array of ILine objects representing the spread's edges
 */
function extractSpreadGuidelines(spread: eXpress.core.Spread) {
	const spreadRect: eXpress.math.IRectWithAngle = {
		left: spread.bounds.left,
		top: spread.bounds.top,
		width: spread.bounds.width,
		height: spread.bounds.height,
		angle: 0, // Spread is always axis-aligned
	};
	return extractEdgesFromRect(spreadRect, "page-edge");
}

function getGuidelineSegmentClippedToSpread(
	line: eXpress.creator.snap.LineSegment2,
	spreadBounds: { left: number; top: number; width: number; height: number },
): { start: eXpress.creator.snap.Vector2; end: eXpress.creator.snap.Vector2 } | null {
	// Clone so we don't mutate cached/shared line segments.
	const clipped = LineSegment2.create(line.start.clone(), line.end.clone());
	const ok = clipped.clipToRect(
		spreadBounds.left,
		spreadBounds.top,
		spreadBounds.width,
		spreadBounds.height,
		true,
	);
	if (!ok) {
		clipped.start.free();
		clipped.end.free();
		clipped.free();
		return null;
	}

	// Detach the clipped points so we can safely free the LineSegment2 back to the cache.
	// NOTE: caller owns returned vectors and must free them.
	const start = clipped.start.clone();
	const end = clipped.end.clone();
	clipped.start.free();
	clipped.end.free();
	clipped.free();
	return { start, end };
}

function removeGuidelines(element: SVGSVGElement) {
	element.innerHTML = "";
}

function drawGuidelines(
	selectedZone: eXpress.core.IZone | undefined,
	dipToScreenTransform: Transform,
	element: SVGSVGElement,
	currentHandle: ZoneSelectionBoxHandlers | undefined,
	transformType: TransformType,
): ILine[] {
	const lines: ILine[] = [];
	if (!selectedZone) return lines;

	const spreadBounds = selectedZone.spread.bounds;
	const zoneTransform = selectedZone.transform();
	const zoneRect = zoneTransformToSnappingRect(zoneTransform);
	const selectedZoneAngle = zoneTransform.angle;

	const isRotating = transformType === TransformType.Rotate;

	// Extract guidelines from spread edges (spread is always axis-aligned, i.e., angle = 0).
	// During rotation: always include spread edges (the zone may rotate to align with them).
	// During move/resize: only include if zone angle is compatible with 0° (multiple of 90°).
	if (isRotating || areAnglesCompatible(selectedZoneAngle, 0)) {
		lines.push(...extractSpreadGuidelines(selectedZone.spread));
	}

	// Extract guidelines from other zones.
	// During rotation: include ALL zones regardless of angle (desktop's updateAlignmentLines behavior).
	// The zone's angle is actively changing, so it might align with any other zone edge.
	// During move/resize: only include zones with compatible angles (differ by multiple of 90°).
	const allZonesInSpread = selectedZone.spread.getAllZones({
		mapFilter: (z) =>
			eXpress.core.isPhotoZone(z) ||
			eXpress.core.isAnyTextZone(z) ||
			eXpress.core.isSymbolZone(z)
				? z
				: undefined,
	});
	allZonesInSpread.forEach((zone) => {
		if (zone === selectedZone) return;

		// During move/resize: only include zones with compatible angles
		if (!isRotating && !areAnglesCompatible(selectedZoneAngle, zone.angle)) return;

		// Create zone rect including border thickness
		const otherZoneRect = zoneTransformToSnappingRect(zone.transform());

		// Extract rotated edges from compatible zones
		lines.push(...extractZoneGuidelines(otherZoneRect));
	});

	// During rotation: add a 45° diagonal snap line (matching desktop's appendLines behavior).
	// This enables 45° snapping through the line mechanism AND provides a visual guideline.
	// 90° snapping is already provided by the spread's own rectangular edges.
	if (isRotating) {
		const startVec = Vector2.create(-100, 0);
		const endVec = Vector2.create(0, -100);
		lines.push({
			lineSegment: LineSegment2.create(
				startVec,
				endVec,
				MAX_SNAP_DISTANCE_DIP,
				rotationToNormalizedDot(MAX_ROTATION_DEGREES),
			),
			type: "page-edge",
		});
	}
	// remove existing lines before drawing
	removeGuidelines(element);

	// Tolerance in DIP for showing snapping lines (same as desktop's maxDist default)
	const SNAP_LINE_TOLERANCE = 1;
	const rotationAlignedLines =
		transformType === TransformType.Rotate
			? getRotationAlignedLines(lines, zoneRect, SNAP_LINE_TOLERANCE)
			: undefined;

	// Get rotated corners once for all line checks
	// getRotatedCorners returns array in order: TopLeft, TopRight, BottomRight, BottomLeft
	const rotatedCorners = getRotatedCorners(zoneRect);

	// create SVGLines and add them to view
	lines.forEach((line) => {
		let shouldShowLine =
			transformType === TransformType.Rotate ? !!rotationAlignedLines?.has(line) : false;

		if (shouldShowLine || transformType === TransformType.Rotate) {
			// During rotation: only show lines from the rotationAlignedLines set (edge-parallel lines).
			// Never fall through to corner-proximity checks — those are for move/resize only.
		} else if (currentHandle === ZoneSelectionBoxHandlers.None || currentHandle === undefined) {
			// Moving a zone: show lines that align to any corner of the zone
			for (let i = 0; i < rotatedCorners.length && !shouldShowLine; i++) {
				const corner = rotatedCorners[i];
				const cornerVec = Vector2.create(corner.left, corner.top);
				// IMPORTANT: snapping uses closestPoint(..., true) (infinite line), not just the segment.
				// Use full=true here as well, otherwise guidelines only show when segments overlap.
				const distance = line.lineSegment.distance(cornerVec, true);
				cornerVec.free();
				if (distance <= SNAP_LINE_TOLERANCE) {
					shouldShowLine = true;
				}
			}
		} else if (!shouldShowLine) {
			// Resizing a zone: show lines that align with the edges being resized
			// Get the corners that are moving during this resize operation
			const movingCornerIds = getMovingCornersForHandle(
				currentHandle ?? ZoneSelectionBoxHandlers.None,
			);

			for (let i = 0; i < movingCornerIds.length && !shouldShowLine; i++) {
				const corner = rotatedCorners[movingCornerIds[i]];
				const cornerVec = Vector2.create(corner.left, corner.top);
				const distance = line.lineSegment.distance(cornerVec, true);
				cornerVec.free();
				if (distance <= SNAP_LINE_TOLERANCE) {
					shouldShowLine = true;
				}
			}
		}

		if (shouldShowLine) {
			// Extend the guideline across the full spread by clipping the infinite line to the spread bounds.
			const clipped = getGuidelineSegmentClippedToSpread(line.lineSegment, spreadBounds);
			if (!clipped) return;

			const startPoint = dipToScreenTransform.transformPoint({
				left: clipped.start.x,
				top: clipped.start.y,
			});
			const endPoint = dipToScreenTransform.transformPoint({
				left: clipped.end.x,
				top: clipped.end.y,
			});

			const svgLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
			svgLine.setAttribute("x1", `${startPoint.left}`);
			svgLine.setAttribute("y1", `${startPoint.top}`);
			svgLine.setAttribute("x2", `${endPoint.left}`);
			svgLine.setAttribute("y2", `${endPoint.top}`);

			svgLine.classList.add(
				line.type === "zone-edge" ? "guideline--zone-edge" : "guideline--page-edge",
			);
			element.appendChild(svgLine);

			clipped.start.free();
			clipped.end.free();
		}
	});

	// return all lines, even the ones we don't show
	return lines;
}

export const GuidelineOverlay = memo(function GuidelineOverlay({
	ref,
	..._props
}: {
	ref?: React.Ref<IGuidelineOverlayRef>;
}) {
	const divRef = useRef<SVGSVGElement>(null);
	useImperativeHandle(ref, () => {
		const el = divRef.current;
		if (!el) return null as unknown as IGuidelineOverlayRef;

		return {
			el,
			drawGuidelines: (
				selectedZone: eXpress.core.IZone | undefined,
				dipToScreenTransform: Transform,
				currentHandle: ZoneSelectionBoxHandlers,
				transformType: TransformType,
			) => {
				return drawGuidelines(
					selectedZone,
					dipToScreenTransform,
					el,
					currentHandle,
					transformType,
				);
			},
			removeGuidelines: () => {
				removeGuidelines(el);
			},
		};
	});

	return <svg className="guideline-overlay" ref={divRef} />;
});

GuidelineOverlay.displayName = "GuidelineOverlay";
